import { useState } from 'react';

import type { Birthday } from '@contracts/index';
import { DAYS_IN_MONTH, MONTH_NAMES, formatBirthday } from '../format';
import { ChevronDownIcon } from '../icons';
import { Column } from './Wheel';
// C-016 (FND-1611): the fold the wheel opens and closes through on «Профиль».
import { FoldWhere } from './Unfold';

/*
 * **A port of `legacy/frontend/src/screens/BirthdayWheel.tsx`, whole but for its `:3`.** F-022 T5:
 * the day and month wheels «Профиль» draws under its fields, which T6 puts inside `ProfileScreen`.
 * Nothing calls it before then. The old file has no header, so this one stands below its import
 * block rather than above it.
 *
 * Their `:3` imports `Birthday` from `../api`. It is a server shape, so here it comes from
 * `@contracts/index` (ADR-0011); `app/src/api.ts` does not export it. That is the one seam. Their
 * `:1-2`, their `:4-6` and their `:7` to the end are theirs byte for byte, and
 * `scripts/ported-partial.txt` holds each region.
 *
 * **`frame="field"` has no drawer in this tree.** In the old app it is F-001's only one — the form on
 * «Вступление» — and it is ported here with the rest per the plan's G1 rather than cut out, so the
 * file stays diffable against its original; `BirthdayWheel.test.tsx` holds it by a unit case (AC-28).
 */

/**
 * Day and month, in two wheels, and never a text field.
 *
 * A date input would drag a year in with it, and there is no year here — see
 * backend/app/domain/birthdays.py for why. A month calendar was the other
 * candidate and loses for the same reason: without a year the weekday columns
 * name nothing.
 *
 * The wheels also make the one mistake a person can actually make unreachable:
 * the day column is as long as the chosen month, so 31 April cannot be reached
 * at all and the server's refusal stays a guard rather than something anybody
 * meets.
 *
 * «Не указан» is the first row of the month, because not saying is a legitimate
 * answer and getting back to it has to be possible.
 */
export default function BirthdayWheel({
  frame = 'field',
  value,
  onChange,
}: {
  /**
   * What this copy is drawn inside. `field` is a form on «Вступление», where
   * every label is a quiet sentence-case line; `block` is «Профиль», where the
   * label stands over its block in the service face like the ones beside it.
   */
  frame?: 'field' | 'block';
  value: Birthday | null;
  onChange: (birthday: Birthday | null) => void;
}) {
  const [opened, setOpened] = useState(false);
  const month = value?.month ?? null;
  const days = month === null ? 31 : DAYS_IN_MONTH[month - 1];

  /**
   * Folded shut on «Профиль», since 2026-08-29, and open on «Вступление».
   *
   * The columns catch a finger, and on «Профиль» they stood in the middle of a
   * screen somebody scrolls past: a scroll that landed on the wheel spun the
   * date that was already chosen, and this screen saves on the tap that picks,
   * so the wrong date went to the server before anybody read it. «Вступление»
   * does not scroll past it, and the date is what that screen is asking for.
   */
  const block = frame === 'block';
  const open = opened || !block;

  function pickMonth(index: number): void {
    // The first row is the absence of a date, and picking it clears the day too.
    if (index === 0) return onChange(null);
    // 31 January picked, then February: the day is clamped rather than the
    // whole birthday being dropped, which would read as the form forgetting it.
    onChange({ day: Math.min(value?.day ?? 1, DAYS_IN_MONTH[index - 1]), month: index });
  }

  function pickDay(index: number): void {
    if (month === null) return;
    onChange({ day: index + 1, month });
  }

  return (
    <div className={block ? 'picker-row' : 'field'}>
      {block ? (
        // The value is the way in, and there is no second one. A full-width
        // «Другая дата» stood under this line until 2026-09-02 saying what the
        // line already said, at 46px and in the weight of the screen's main
        // action - on a screen whose other rows are a label over a field.
        <button
          className="when-head"
          // A row this wide has no edge to scale against: the press is the fade
          // every control drawn as words takes.
          data-press="fade"
          aria-expanded={open}
          onClick={() => setOpened(!open)}
        >
          <span className="picker-label">День рождения</span>
          {/* Quiet and not the accent where there is no date: grey is what has
              not been chosen, and «не указан» is the absence of a choice rather
              than one of the values. */}
          <span className={value === null ? 'when-value when-value--blank' : 'when-value'}>
            {value === null ? 'не указан' : formatBirthday(value)}
            {/* Turned rather than swapped: the row survives the fold, so its
                chevron is the same element on both sides of it. */}
            <ChevronDownIcon size={14} turned={open} />
          </span>
        </button>
      ) : (
        <span>День рождения</span>
      )}

      {/* C-016 (FND-1611): on «Профиль» the wheel opens and closes under its row
          through `Unfold` rather than arriving whole in one frame. On «Вступление»
          (`frame='field'`) it is always open and stands as it did. */}
      <FoldWhere fold={block} open={open}>
        <div className="wheel">
          {/* Under the columns, not over them: the band marks the middle row and
              the row's own text has to stay on top of it. */}
          <span className="wheel-band" aria-hidden="true" />
          <div className="wheel-cols">
            <Column
              label="День рождения: число"
              // A dash rather than «1» while no month is chosen, and the same
              // mistake in a new shape: the old pair of selects showed the first
              // option of a list nobody had chosen from, so the field read «1»
              // beside «не указан», which is a first of nothing.
              items={month === null ? ['—'] : DAY_LABELS.slice(0, days)}
              index={month === null ? 0 : (value?.day ?? 1) - 1}
              onPick={pickDay}
              mono
              // Grey is what has not been chosen: a day without a month is not a
              // date, so until a month is picked this column states nothing and
              // does not answer a finger either.
              quiet={month === null}
            />
            <Column
              label="День рождения: месяц"
              items={MONTH_ITEMS}
              index={month ?? 0}
              onPick={pickMonth}
              wide
            />
          </div>
          {/* The rows do not stop at the edge of the panel, they dissolve into
              it - which is what says the column continues past what is drawn. */}
          <span className="wheel-fade wheel-fade--top" aria-hidden="true" />
          <span className="wheel-fade wheel-fade--bottom" aria-hidden="true" />
        </div>
      </FoldWhere>
      {/* Under the wheel rather than anywhere else on the screen. It is the
          sentence that says why the field is worth filling in, so it belongs
          where somebody is deciding whether to - the owner's call of
          2026-08-19, after it had been sitting at the foot of «Я» next to
          nothing in particular. */}
      <p className="hint">
        {value === null
          ? 'Не обязательно. Если не указать, хор не узнает, когда тебя поздравить.'
          : 'В этот день хор увидит его на «Расписании».'}
      </p>
    </div>
  );
}

const DAY_LABELS = Array.from({ length: 31 }, (_, index) => String(index + 1));
const MONTH_ITEMS = ['не указан', ...MONTH_NAMES];
